import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function sortByLabel<T>(items: T[], getLabel: (item: T) => string): T[] {
  return [...items].sort((a, b) => getLabel(a).localeCompare(getLabel(b), undefined, { sensitivity: 'base' }));
}

export function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function toInputDate(iso: string) {
  return iso.slice(0, 10);
}

// Sunday-based week number; week 1 contains Jan 1.
// Uses Date.UTC to avoid DST offsets distorting the day count.
export function getWeekNumber(dateStr: string): number {
  const [year, month, day] = dateStr.split('-').map(Number);
  const utcMs = Date.UTC(year, month - 1, day);
  const utcYearStart = Date.UTC(year, 0, 1);
  const startDay = new Date(utcYearStart).getUTCDay(); // 0 = Sun
  const dayOfYear = (utcMs - utcYearStart) / 86_400_000;
  return Math.floor((dayOfYear + startDay) / 7) + 1;
}

export function weekToDateRange(year: number, week: number): { from: string; to: string } {
  const utcYearStart = Date.UTC(year, 0, 1);
  const startDay = new Date(utcYearStart).getUTCDay(); // 0 = Sun
  const weekStartOffset = (week - 1) * 7 - startDay;
  const fromMs = utcYearStart + Math.max(0, weekStartOffset) * 86_400_000;
  const toMs = utcYearStart + (weekStartOffset + 6) * 86_400_000;
  const fmt = (ms: number) => new Date(ms).toISOString().slice(0, 10);
  return { from: fmt(fromMs), to: fmt(toMs) };
}

export function getWeekDatesForDate(date: string): string[] {
  const weekStart = new Date(`${date}T12:00:00Z`);
  weekStart.setUTCDate(weekStart.getUTCDate() - weekStart.getUTCDay());

  return Array.from({ length: 7 }, (_, index) => {
    const weekDate = new Date(weekStart);
    weekDate.setUTCDate(weekStart.getUTCDate() + index);
    return weekDate.toISOString().slice(0, 10);
  });
}

export function getWorkingDatesForWeek(date: string): string[] {
  return getWeekDatesForDate(date).slice(0, 5);
}

export function isWeekendDate(date: string): boolean {
  const day = new Date(`${date}T12:00:00Z`).getUTCDay();
  return day === 5 || day === 6;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function getDayName(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  return DAY_NAMES[new Date(year, month - 1, day).getDay()];
}
